// ============================================================================
// CAMADA DE INTEGRAÇÃO MULTI-CLIENTE (Padrão Provider / Adapter)
// ----------------------------------------------------------------------------
// Cada empresa tem um "tipo" de integração definido em window.configIntegracaoEmpresa
// (ex.: { tipo: 'bluesoft', tenant: 'minipreco' }). Cada tipo é um PROVIDER que
// implementa o MESMO contrato abaixo. Para suportar um novo cliente / ERP / banco,
// basta registrar um novo objeto em window.PROVIDERS — nenhum outro arquivo muda.
//
// CONTRATO DO PROVIDER:
//   async buscarProduto(gtin, lojaId, lojaNome)
//        -> { descricao, custo, preco, estoque, gtin, gtin_validade_curta }
//        -> ou null / { erro:true, mensagem } quando não encontrar
//   async buscarMovimentacao(gtin, lojaId)  -> objeto de movimentação (ou {})
//   suportaAutoPreenchimento: bool          -> se bipar GTIN preenche os campos
//
// COMO O PROVIDER É ESCOLHIDO:
//   window.getProvider() lê configIntegracaoEmpresa.tipo. Sem config -> 'nenhum'.
//
// COMO ADICIONAR UM CLIENTE NOVO (ex.: Condor/SAP):
//   1. Implemente os métodos do contrato num novo objeto e registre em PROVIDERS.
//   2. Defina a integração da empresa (tipo + proxyUrl + params). Hoje isso vem do
//      e-mail no login (app.js); a Fase 2 move essa config para o Firestore.
//   3. Integrações que falam com servidores externos (SAP, etc.) precisam de um
//      proxy/middleware próprio — leia a URL de configIntegracaoEmpresa.proxyUrl.
// ============================================================================

(function () {
    "use strict";

    // [FIX 2026-07] Sem fallback pro tenant da Minipreço (mesmo padrão E2) — só é chamada
    // dentro dos métodos do provider 'bluesoft' (já implica tipo==='bluesoft'), mas uma
    // empresa bluesoft SEM .tenant configurado (erro de cadastro) não deve herdar o tenant
    // de outro cliente por acidente; melhor retornar vazio (busca não acha nada) do que
    // silenciosamente consultar o ERP errado.
    const tenantAtual = () => window.configIntegracaoEmpresa?.tenant || "";

    window.PROVIDERS = {

        // ── BLUESOFT (Minipreço) — embrulha o motor existente, sem mudar comportamento ──
        bluesoft: {
            nome: "Bluesoft ERP",
            suportaAutoPreenchimento: true,
            // [Controladoria] buscarProduto já devolve custo+preço — nenhum método novo
            // precisa ser implementado pra Auditoria de Margem usar este provider.
            capacidades: { custoPreco: true },
            async buscarProduto(gtin, lojaId /*, lojaNome */) {
                return window.buscarProdutoOtimizado(gtin, lojaId, tenantAtual());
            },
            async buscarMovimentacao(gtin, lojaId) {
                return (typeof window.buscarMovimentacaoProduto === 'function')
                    ? window.buscarMovimentacaoProduto(gtin, lojaId, tenantAtual())
                    : {};
            }
            // Nota: o relatório de negativos do Bluesoft hoje é tratado por
            // bluesoftCalcularDistorcoes (fetch + render juntos). Migra para o
            // contrato do provider na Fase 2, quando um 2º cliente precisar.
        },

        // ── SEM INTEGRAÇÃO — entrada manual (sem autofill). Comportamento fiel ao atual. ──
        nenhum: {
            nome: "Sem integração (manual)",
            suportaAutoPreenchimento: false,
            capacidades: { custoPreco: false },
            async buscarProduto() { return null; },
            async buscarMovimentacao() { return {}; }
        },

        // ── SHEETS — cliente com banco no Google Sheets (SLOT: implementar na Fase futura) ──
        sheets: {
            nome: "Banco em Google Sheets",
            suportaAutoPreenchimento: true,
            // buscarProdutoBancoLocal devolve {custo, preco} quando a planilha tem essas
            // colunas — mas não é garantido (planilha do cliente pode não ter custo). Fica
            // 'false' por padrão; resolverFonteFinanceira cai pra 'import' se o chamador
            // tentar 'provider' e não achar os campos na resposta.
            capacidades: { custoPreco: false },
            async buscarProduto(gtin) {
                if (typeof window.buscarProdutoBancoLocal === 'function') {
                    return window.buscarProdutoBancoLocal(gtin);
                }
                return { erro: true, mensagem: "Provider 'sheets' ainda não implementado." };
            },
            async buscarMovimentacao() { return {}; }
        },

        // ── SAP — slot para futura integração (ex.: Condor). Implementar criando um
        //    middleware/proxy SAP e consumindo configIntegracaoEmpresa.proxyUrl. ──
        sap: {
            nome: "SAP",
            suportaAutoPreenchimento: true,
            capacidades: { custoPreco: false },
            async buscarProduto() { return { erro: true, mensagem: "Provider 'sap' ainda não implementado." }; },
            async buscarMovimentacao() { return {}; }
        }
    };

    // Retorna o provider ativo conforme a empresa logada (default seguro: 'nenhum').
    window.getProvider = function () {
        const tipo = window.configIntegracaoEmpresa?.tipo || 'nenhum';
        return window.PROVIDERS[tipo] || window.PROVIDERS.nenhum;
    };

    // ────────────────────────────────────────────────────────────────────────
    // INTEGRAÇÃO POR EMPRESA (registro no código)
    // ----------------------------------------------------------------------------
    // Mapa company_name (EXATO, como cadastrado/gravado no perfil) -> config de
    // integração. Sobrepõe o roteamento por e-mail feito no login (app.js). Para
    // plugar um cliente novo cujo "banco" é uma planilha, basta uma linha aqui.
    //   - { tipo: 'sheets' }                 -> usa a planilha-catálogo PADRÃO (multiempresa).
    //   - { tipo: 'sheets', sheetId: 'ID' }  -> usa uma planilha Google PRÓPRIA do cliente.
    // (A migração desta config para o Firestore + tela admin é a "Fase 2".)
    //
    // [FIX 2026-07 — E1] `mapaLojas` (id numérico Bluesoft -> nome da loja, formato do antigo
    // DICIONARIO_LOJAS hardcoded em app.js) também pode ser definido aqui OU em
    // empresas/{nome}.integracao.mapaLojas no Firestore (esta última sobrepõe — mesma cascata
    // dos campos tipo/tenant/regras, sem código extra: o spread de resolverConfigEmpresa já
    // copia qualquer campo dentro de `integracao`). Só necessário pra um 2º cliente Bluesoft
    // com lojas diferentes do Grupo Minipreço — window.traduzirLojaParaId/traduzirIdParaLoja
    // (app.js) usam o mapa da empresa logada quando presente, senão caem no dicionário
    // hardcoded (retrocompatível, Minipreço não precisa configurar nada). Ex.:
    //   'Cliente Bluesoft Novo': { tipo: 'bluesoft', tenant: 'xyz',
    //       mapaLojas: { '1': 'LOJA CENTRO', '2': 'LOJA NORTE' } }
    // ────────────────────────────────────────────────────────────────────────
    // espelharInventarioSheets: além do Firestore, espelha cada bipagem + o fechamento do
    // inventário no Web App legado (backend-sheets/Código.js -> planilha ID_INVENTARIO).
    window.INTEGRACOES_POR_EMPRESA = {
        'Lucroseguro Inventarios': { tipo: 'sheets', regras: { espelharInventarioSheets: true } },
        // 'Cliente XPTO': { tipo: 'sheets', sheetId: '1AbCdEfGhIjKlMnOpQrStUvWxYz', regras: { ... } },
    };

    // Resolve a integração de uma empresa pelo nome. Retorna a config ou null.
    window.resolverIntegracaoEmpresa = function (nomeEmpresa) {
        const nome = String(nomeEmpresa || '').trim();
        return window.INTEGRACOES_POR_EMPRESA[nome] || null;
    };

    // ────────────────────────────────────────────────────────────────────────
    // REGRAS POR EMPRESA (capacidades / flags de comportamento)
    // ----------------------------------------------------------------------------
    // Cada empresa liga só as regras que usa. O código consulta empresaTemRegra()
    // em vez de checar o nome da empresa ("includes('minipreco')"). Default: tudo
    // desligado — uma empresa nova começa sem regras especiais.
    // ────────────────────────────────────────────────────────────────────────
    window.REGRAS_PADRAO = {
        traduzLojaNumero: false,        // número da loja no ERP -> nome (Bluesoft/Minipreço)
        dashboardNegativos: false,      // habilita o painel de estoque negativo (Bluesoft)
        espelharInventarioSheets: false, // espelha o inventário na planilha do cliente (Sheets)
        // [CUSTO ZERADO 2026-07-31] Item lançado no inventário com custo 0 (produto EXISTE no
        // ERP mas o custo não está cadastrado) não entra na contagem: é desviado pra aba
        // "Custo Zerado", pro gestor mandar o setor fiscal/cadastro regularizar. Sem isso, a
        // divergência aparece valendo R$ 0,00 e uma perda real fica invisível no relatório.
        // Pedido do usuário em 2026-07-31 — SÓ Minipreço por enquanto.
        custoZeradoDesvia: false,
        // [2026-07-31] Operador apaga a leitura DIRETO na lista de bipados, sem o fluxo de
        // aprovacao (ajustes_pendentes). Vale SO pro ITEM — excluir o inventario inteiro
        // continua exigindo aprovacao. Pedido do usuario; a exclusao segue auditada em
        // audit_log pelo trigger generico da colecao inventario.
        operadorExcluiItem: false,
        // [2026-08-19] Bloqueia SALVAR a bipagem quando o GTIN não foi localizado no ERP. Sem
        // isso, um operador que digita uma descrição própria por cima de "Produto não
        // localizado" (o campo não é readonly) engana `desviarCustoZerado`: como custo=0 E
        // descrição preenchida, o item era tratado como "existe no ERP mas sem custo" e ia
        // pra aba Custo Zerado — quando na verdade o produto nunca foi encontrado. Pedido do
        // usuário em 2026-08-19 — SÓ Minipreço.
        bloqueiaGtinNaoEncontrado: false,
        // [PERDA VIA TROCAS 2026-08-26] Muda o que acontece com o item de PERDA ao mandar o
        // inventário pro ERP: em vez de zerar o produto (ajuste de inventário), ele é
        // transferido pro estoque de TROCAS da loja e vai pra uma sub-aba de Auditoria, onde
        // só vira perda depois de decidido. GANHO continua ajustando no ERP como sempre.
        // Provado na Fase 0 (2026-08-26) que o ERP suporta ida e volta: a troca não gera nota
        // fiscal e o estorno devolve o saldo (69 -> 68 -> 69 na loja 305).
        // ⚠️ NASCE DESLIGADA E FICA DESLIGADA ATÉ A FASE 2 — não basta ligar aqui: sem o
        // desvio implementado no envio ao ERP, ligar esta regra não faz nada; e depois de
        // implementado, ligá-la muda o resultado FINANCEIRO do inventário (a perda deixa de
        // entrar no indicador no fechamento e passa a depender da auditoria).
        perdaViaTrocas: false,
        // [TRAVA 2 2026-10-13] Produto com saldo NEGATIVO que está numa nota de transferência ainda não
        // acatada na loja NÃO entra no inventário: vai para a sub-aba "Aguardando nota" e sai sozinho
        // quando a nota é acatada (js/aguardando-nota.js). Depende do índice gravado pela
        // rotinaNotasFiscais (`nf_pendentes_produtos`). Ligada no Firestore (empresas/{nome}.regras).
        aguardandoNotaPendente: false,
        // [QUEBRA VIA TROCAS 2026-09-23] A quebra lançada no módulo Quebras (formulário e CSV)
        // também tira a mercadoria do saldo no ERP, mandando-a pro estoque de TROCAS com o motivo
        // real (`regras.quebraMotivosTroca`, mapa motivo → motivoTrocaKey, + `trocaSetorKey`).
        // Aqui só liga a UI; quem decide de verdade é a regra lida NO SERVIDOR. Nasce desligada
        // até o usuário informar as chaves dos motivos (a API não deixa ler — permissão 4010).
        quebraViaTrocas: false,
        // [EXPEDIÇÃO 2026-08-25] Liga a sub-aba "Conferência de Produto" em Docas (auditoria
        // de expedição: bipa a carga que SAI, desvia item sem custo / sem saldo, e ao encerrar
        // monta a nota de transferência para a loja destino). Depende de provider que devolva
        // custo E saldo por loja (`capacidades.custoPreco`) — sem isso as duas travas não têm
        // como decidir nada e a tela viraria só um bloco de notas. Pedido do usuário em
        // 2026-08-25 — SÓ Minipreço por enquanto.
        expedicaoConferencia: false,
        // [EXPEDIÇÃO 2026-08-25] Emissão REAL da NF-e de transferência no ERP ao encerrar a
        // contagem. Com `false` (padrão) o encerramento roda em MODO ENSAIO: monta e grava o
        // payload, mostra na tela o que SERIA enviado, e não chama o ERP. Decisão do usuário
        // em 2026-08-25: emitir NF-e é irreversível e não dá pra testar sem emitir nota de
        // verdade — então a primeira volta é ensaio, e ligar a emissão é trocar esta flag
        // (mais a ação na Cloud Function, que ainda não existe). NÃO ligar sem isso.
        expedicaoEmiteNf: false,
        // [EXPEDIÇÃO 2026-08-25] Para quem vai o e-mail de faturamento da NF-e de
        // transferência. O spec do ERP diz que o campo é OPCIONAL e "necessário caso a loja
        // destino não possua e-mail de envio NF-e cadastrado". Vazio = cai no e-mail de quem
        // encerrou a contagem, que quase nunca é o destinatário certo (é o conferente da
        // doca): preencher com o e-mail do setor fiscal/faturamento da empresa.
        expedicaoEmailFaturamento: '',
        // [Controladoria] Fonte dos dados financeiros por módulo. '' = auto (resolverFonteFinanceira
        // decide pela capacidade do provider); força um valor só se o auto-detect escolher errado.
        margemFonte: '',                // '' | 'provider' | 'import' | 'manual'
        margemPisoPct: null             // null = usa o padrão (15%) definido em obterPisoMargem()
    };

    // Remove acentos para casar nomes/e-mails de forma robusta (NFD + faixa de diacríticos).
    const _norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

    // Resolve a configuração final de integração de uma empresa, em cascata de
    // prioridade (a de baixo sobrepõe a de cima):
    //   1) roteamento por e-mail   (Minipreço -> bluesoft)        [retrocompat]
    //   2) registry em código      (INTEGRACOES_POR_EMPRESA)      [retrocompat]
    //   3) Firestore               (empresas/{nome}.integracao)   [fonte da verdade]
    // Retorna { tipo, tenant?, sheetId?, proxyUrl?, nome?, regras{} } ou null.
    window.resolverConfigEmpresa = function ({ empresa, email, firestoreData } = {}) {
        const nome = String(empresa || '').trim();
        const mail = _norm(email);
        let cfg = null;
        let regras = {};

        // 1) e-mail (Minipreço)
        if (mail.includes('grupominipreco.com.br') || mail.includes('minipreco')) {
            cfg = { tipo: 'bluesoft', tenant: 'minipreco', nome: nome || 'Grupo Minipreço' };
            // `expedicaoEmiteNf` fica de fora de propósito (default false = modo ensaio):
            // ligar a emissão fiscal real é decisão explícita, nunca herdada do roteamento.
            regras = { traduzLojaNumero: true, dashboardNegativos: true, custoZeradoDesvia: true, operadorExcluiItem: true, bloqueiaGtinNaoEncontrado: true, expedicaoConferencia: true };
        }

        // 2) registry em código (por nome de empresa)
        const reg = window.INTEGRACOES_POR_EMPRESA[nome] || null;
        if (reg) {
            const { regras: regrasReg, ...resto } = reg;
            cfg = { ...(cfg || {}), ...resto, nome };
            regras = { ...regras, ...(regrasReg || {}) };
        }

        // 3) Firestore (empresas/{nome}) — sobrepõe tudo quando existir
        if (firestoreData && firestoreData.integracao) {
            cfg = { ...(cfg || {}), ...firestoreData.integracao, nome };
            regras = { ...regras, ...(firestoreData.regras || {}) };
        }

        if (!cfg) return null;
        cfg.regras = { ...window.REGRAS_PADRAO, ...regras };
        return cfg;
    };

    // True se a empresa tem a regra ligada. Funciona no login do operador
    // (configIntegracaoEmpresa) e na Visão Consultor do admin (resolve pelo nome
    // via cache window._empresasConfig, populado no login do admin). Mantém um
    // fallback p/ Minipreço por nome/e-mail enquanto o Firestore não está populado.
    window.empresaTemRegra = function (regra, empresaNome) {
        const cfg = window.configIntegracaoEmpresa;
        const nomeAlvo = String(empresaNome != null ? empresaNome : (window.currentUserEmpresa || '')).trim();
        const mesmaEmpresa = (empresaNome == null) || (nomeAlvo === String(window.currentUserEmpresa || '').trim());

        // 1) sessão do operador (config já resolvida no login)
        if (cfg && cfg.regras && mesmaEmpresa && (regra in cfg.regras)) {
            return !!cfg.regras[regra];
        }
        // 2) cache das empresas (admin / consultor)
        const cache = window._empresasConfig && window._empresasConfig[nomeAlvo];
        if (cache && cache.regras && (regra in cache.regras)) {
            return !!cache.regras[regra];
        }
        // 3) fallback retrocompatível: Minipreço (antes de popular o Firestore)
        // `expedicaoEmiteNf` NÃO entra nesta lista: o fallback existe pra não quebrar tela
        // enquanto o Firestore não está populado, e uma emissão fiscal real jamais deve ser
        // ligada por fallback — só por configuração explícita.
        if (regra === 'traduzLojaNumero' || regra === 'dashboardNegativos' || regra === 'custoZeradoDesvia' || regra === 'operadorExcluiItem' || regra === 'bloqueiaGtinNaoEncontrado' || regra === 'expedicaoConferencia') {
            const mail = _norm(window.auth?.currentUser?.email);
            if (_norm(nomeAlvo).includes('minipreco') || mail.includes('minipreco')) return true;
        }
        return false;
    };

    // ────────────────────────────────────────────────────────────────────────
    // FONTE DE DADOS FINANCEIROS (Controladoria) — Fase 0 do Kit Controladoria
    // ----------------------------------------------------------------------------
    // Nenhum módulo de Controladoria (Auditoria de Margem, e depois Orçado/DRE/
    // Conciliação/Verbas) fala com uma API diretamente. Todos passam por
    // resolverFonteFinanceira(modulo), que devolve UM dos 4 backends abaixo — a troca de
    // fonte é uma configuração por empresa (regra 'xFonte'), nunca uma mudança de código:
    //   'manual'   -> gestor digita os números num painel (SEMPRE disponível, é o piso de
    //                 segurança: garante que o módulo funciona em qualquer empresa desde o
    //                 dia 1, sem nenhuma integração).
    //   'import'   -> gestor sobe planilha/CSV (importarFinanceiro), qualquer ERP consegue
    //                 exportar algo compatível.
    //   'provider' -> o provider da empresa (js/providers.js PROVIDERS) já expõe o dado via
    //                 capacidades.
    //   'firestore'-> o LucroSeguro já tem o dado internamente (ex.: quebra/negativos).
    // Regra vazia ('') = auto-detect: usa 'provider' se a empresa tiver um provider com a
    // capacidade certa pro módulo, senão cai em 'import'. Regra explícita SEMPRE vence o
    // auto-detect (permite forçar import mesmo com provider capaz, ex.: dado do provider
    // não bate com o real por algum motivo operacional).
    // ────────────────────────────────────────────────────────────────────────
    const CAPACIDADE_POR_MODULO = {
        margem: 'custoPreco',
    };
    window.resolverFonteFinanceira = function (modulo) {
        const regraForcada = window.configIntegracaoEmpresa?.regras?.[modulo + 'Fonte'];
        if (regraForcada) return regraForcada;
        const capacidadeNecessaria = CAPACIDADE_POR_MODULO[modulo];
        const provider = window.getProvider ? window.getProvider() : null;
        if (capacidadeNecessaria && provider?.capacidades?.[capacidadeNecessaria]) return 'provider';
        return 'import';
    };

    // Piso de margem (%) configurável por empresa (regras.margemPisoPct); default 15%.
    window.obterPisoMargem = function () {
        const v = window.configIntegracaoEmpresa?.regras?.margemPisoPct;
        return (typeof v === 'number' && v > 0) ? v : 15;
    };

    // ────────────────────────────────────────────────────────────────────────
    // IMPORT FINANCEIRO UNIVERSAL — o caminho "sem API nenhuma" pros módulos de
    // Controladoria. Espera um arquivo .xlsx/.csv com cabeçalho na 1ª linha; casa colunas
    // pelo NOME (case/acento-insensível), na ordem de `camposEsperados`. Reusa o SheetJS já
    // carregado sob demanda por window.carregarSheetJS (js/analise-produto.js) — mesmo
    // padrão de carga lazy, só baixa a lib quando alguém realmente importa uma planilha.
    // Retorna array de objetos { [campo]: valor, ... } (linhas sem os campos obrigatórios
    // presentes são descartadas). Não decide POR SI o schema (não é "mapaColunas" por
    // empresa) — cada módulo passa o schema que precisa; deliberadamente simples: um cliente
    // sem provider recebe um modelo de planilha (3-4 colunas nomeadas) e preenche, não
    // precisa configurar mapeamento nenhum.
    // ────────────────────────────────────────────────────────────────────────
    window.importarFinanceiro = async function (file, camposEsperados) {
        const XLSX = await window.carregarSheetJS();
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const primeiraAba = wb.SheetNames[0];
        const linhas = XLSX.utils.sheet_to_json(wb.Sheets[primeiraAba], { defval: '' });

        const achaChave = (linha, nomeCampo) => {
            const alvo = _norm(nomeCampo);
            return Object.keys(linha).find((k) => _norm(k) === alvo || _norm(k).includes(alvo));
        };

        const resultado = [];
        linhas.forEach((linha) => {
            const obj = {};
            let completo = true;
            camposEsperados.forEach((campo) => {
                const chave = achaChave(linha, campo);
                const valor = chave ? linha[chave] : undefined;
                if (valor === undefined || valor === '') completo = false;
                obj[campo] = valor;
            });
            if (completo) resultado.push(obj);
        });
        return resultado;
    };
})();
